import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
// Get asset URLs instead for preloading. vite handles `?url'
import tailwindHref from "./styles/tailwind.css?url";
import primitivesHref from "./styles/tokens/primitives/all-primitives.css?url";
import themeHref from "./styles/tokens/themes/miss-mary.css?url";

import playfairDisplayRegularFont from "./assets/fonts/PlayfairDisplay-Regular.woff2?url";
import sofiaProRegularFont from "./assets/fonts/SofiaPro-Regular.woff2?url";

import "./components/blueprint";
import "./config/storyblok-components";

// Keep this for route-level code/CSS splitting
import { cssBundleHref } from "@remix-run/css-bundle";

import { Cross } from "./assets/icons/Cross";
import { MiniCart } from "@made-people/blueprint-mini-cart";
import { MmSiteSettings } from "./components/global/mm-site-settings";
import { StickyProvider } from "@made-people/blueprint-sticky-wrapper";
import { Footer } from "./components/global/footer";
import { Headers } from "./components/global/headers";
import { Overlay } from "@made-people/blueprint-overlay";
import { StoryblokStoryProvider } from "@made-people/grease-storyblok";
import { ISbStoryData } from "@storyblok/js";
import { Toaster } from "sonner";

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
  useRouteError,
} from "@remix-run/react";

import { Bootstrap } from "./bootstrap";
import {
  GtmScriptHeadTag,
  GtmScriptNoscriptTag,
} from "@made-people/grease-gtm";
import { SizeGuideDrawer } from "./components/features/size-guide/size-guide";
import { LinksFunction } from "@remix-run/node";
import { mmRootLoader } from "./root-loader/mm-root-loader";
import { MmGrowthbookProvider } from "./services/growthbook/mm-growthbook-provider";
import { MmGbState } from "./services/growthbook/mm-gb-state";
import { SearchSidebar } from "@made-people/blueprint-search-sidebar";
import { CookiebotScript } from "./components/global/cookiebot-script";
import { TrustpilotDrawer } from "./components/features/trustpilot/components/trustpilot-drawer";

// import { setEventEmitterDebugMode } from "@made-people/grease-event-emitter";
//setEventEmitterDebugMode(process.env.NODE_ENV !== "production");

export const loader = mmRootLoader;

// Prevent fetching root more than once
export const shouldRevalidate = () => false;

export const links: LinksFunction = () => {
  return [
    ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),
    { rel: "icon", href: "/favicon.ico" },
    {
      rel: "icon",
      type: "image/png",
      sizes: "64x64",
      href: "/favicon-64x64.png",
    },
  ];
};

export function Layout({ children }: { children: React.ReactNode }) {
  const rootLoaderData = useRouteLoaderData<typeof loader>("root");

  if (!rootLoaderData) {
    return null;
  }

  const globalStoryblokStories =
    rootLoaderData.settingsAndContentResponse.globalStoryblokStories;

  return (
    <StoryblokStoryProvider
      story={{} as ISbStoryData}
      insideStoryblokPreview={false}
    >
      <StickyProvider>
        <Bootstrap rootLoaderData={rootLoaderData}>
          <MmGrowthbookProvider
            gbState={rootLoaderData.gbState as MmGbState}
            growthbookConfig={
              rootLoaderData.settingsAndContentResponse.growthbook
            }
          >
            <head>
              <meta charSet="utf-8" />
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
              />
              <meta name="author" content="TRY Stockholm - trystockholm.com" />

              {/* Only preload primary and secondary fonts */}
              <link
                rel="preload"
                href={sofiaProRegularFont}
                as="font"
                type="font/woff2"
                crossOrigin="anonymous"
              />
              <link
                rel="preload"
                href={playfairDisplayRegularFont}
                as="font"
                type="font/woff2"
                crossOrigin="anonymous"
              />

              <link rel="preload" href={primitivesHref} as="style" />
              <link rel="preload" href={themeHref} as="style" />
              <link rel="preload" href={tailwindHref} as="style" />

              <link rel="stylesheet" href={primitivesHref} />
              <link rel="stylesheet" href={themeHref} />
              <link rel="stylesheet" href={tailwindHref} />

              <script
                dangerouslySetInnerHTML={{
                  // This works as long as we don't introduce white spaces before and after the script tag.
                  __html: `window.dataLayer = window.dataLayer || []; function gtag() { dataLayer.push(arguments); }`,
                }}
              />

              <CookiebotScript />

              <GtmScriptHeadTag
                addOriginalLocation={false}
                addFirstBrowserInteractionEvent={false}
              />

              <Meta />
              <Links />
            </head>
            <body className="theme-missmary">
              <GtmScriptNoscriptTag />
              <MiniCart
                settings={{
                  customOverlayId: "mini-cart",
                  heading: {
                    variants: {
                      headingSize: "sm",
                    },
                  },
                  cartTotals: {
                    voucher: {
                      enabled: false,
                    },
                  },
                  template: "lyon",
                  closeIcon: <Cross />,
                }}
              />
              <SizeGuideDrawer />
              <TrustpilotDrawer />
              <SearchSidebar />
              <MmSiteSettings />
              <Headers />
              {children}
              <Footer globalStoryblokStories={globalStoryblokStories} />
              <Overlay />
              <Toaster
                style={{
                  background: "white",
                  color: "black",
                }}
              />
              <ScrollRestoration />
              <Scripts />
            </body>
          </MmGrowthbookProvider>
        </Bootstrap>
      </StickyProvider>
    </StoryblokStoryProvider>
  );
}

function App() {
  return <Outlet />;
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default withSentry(App);
